/**
 * SPILLESTEDET, SETT FRA HUSET SELV (agent A, `docs/SPILLESTED-KONSERT.md` skjerm 1).
 *
 * Saler med faste felt, oppsett, egne felt, filer, standard kveld og mannskapsmal; leien; husets og
 * salens synlighet; kveldene. Alt her er husets egne rader under vanlig RLS. Hva andre hus ser av en
 * sal går gjennom definer-portene (0410) og står ikke her — bortsett fra `VenueRoomSpec`, som er
 * husets forhåndsvisning av nøyaktig det («Se som arrangør»), uten leie og uten mannskapsmal.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';
import { CONCERT_DEAL_TYPES } from '../concerts/deal-math.js';
import { bookingBidStatusSchema } from '../enums.js';
import {
  concertTemplateContentSchema,
  outsideDealSchema,
  type OutsideDeal,
} from './org-concerts.js';

export const VENUE_VISIBILITIES = ['private', 'partners', 'public'] as const;
export type VenueVisibility = (typeof VENUE_VISIBILITIES)[number];
export const venueVisibilitySchema = z.enum(VENUE_VISIBILITIES).meta({ id: 'VenueVisibility' });

/**
 * DE FASTE FELTENE, I FAST REKKEFØLGE. Alle hus har dem, og Spillesteder kan filtrere på dem.
 * `app/venue/room-spec.ts` i web gir hver sin etikett; lista her er det API-et og hendelsen
 * `venue_room.spec_changed` navngir feltene med.
 */
export const ROOM_SPEC_FIELD_KEYS = [
  'capacityStanding',
  'capacitySeated',
  'stageWidthM',
  'stageDepthM',
  'ceilingHeightM',
  'rigging',
  'pa',
  'monitor',
  'mixers',
  'lights',
  'backline',
  'power',
  'loading',
  'nightlinerParking',
  'parking',
  'dressingRooms',
  'bars',
  'accessibility',
  'wheelchairSpaces',
  'hasGrandPiano',
] as const;
export type RoomSpecFieldKey = (typeof ROOM_SPEC_FIELD_KEYS)[number];

/**
 * Alt en endring av spesifikasjonen kan nevne i `changedFields`: de faste feltene, salens
 * identitet og standard kveld, og de to listene (`layouts`, `customFields`).
 */
export const ROOM_SPEC_CHANGE_KEYS = [
  ...ROOM_SPEC_FIELD_KEYS,
  'name',
  'description',
  'city',
  'address',
  'latitude',
  'longitude',
  'defaultDoors',
  'defaultCurfew',
  'defaultGetIn',
  'layouts',
  'customFields',
] as const;
export type RoomSpecChangeKey = (typeof ROOM_SPEC_CHANGE_KEYS)[number];

/** `HH:MM`, 24 timer. */
const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/u;
const venueClockSchema = z.string().regex(CLOCK, 'HH:MM');

const shortText = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value.length === 0 ? null : value))
    .nullish();
const count = (max: number) => z.number().int().min(0).max(max);
const metres = z.number().min(0).max(999.99);

export const venueRoomLayoutDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    capacity: z.number().int(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'VenueRoomLayout' });

export const venueRoomFieldDtoSchema = z
  .object({
    id: z.uuid(),
    label: z.string(),
    value: z.string(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'VenueRoomField' });

export const VENUE_ROOM_FILE_KINDS = ['image', 'document', 'link'] as const;
export type VenueRoomFileKind = (typeof VENUE_ROOM_FILE_KINDS)[number];

export const venueRoomFileDtoSchema = z
  .object({
    id: z.uuid(),
    kind: z.enum(VENUE_ROOM_FILE_KINDS),
    title: z.string(),
    attachmentId: z.uuid().nullable(),
    url: z.string().nullable(),
    filename: z.string().nullable(),
    contentType: z.string().nullable(),
    byteSize: z.number().int().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'VenueRoomFile' });

export const venueCrewTemplateItemDtoSchema = z
  .object({
    id: z.uuid(),
    staffingRoleId: z.uuid().nullable(),
    staffingRoleName: z.string().nullable(),
    title: z.string(),
    needed: z.number().int(),
    /** Minutter relativt til dørene. -120 er to timer før. */
    startsOffsetMinutes: z.number().int(),
    endsOffsetMinutes: z.number().int(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'VenueCrewTemplateItem' });

/** De faste feltene som verdier — den samme formen i DTO-en, spesifikasjonen og skjemaet. */
const roomFixedShape = {
  name: z.string(),
  description: z.string().nullable(),
  city: z.string().nullable(),
  address: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  capacityStanding: z.number().int().nullable(),
  capacitySeated: z.number().int().nullable(),
  stageWidthM: z.number().nullable(),
  stageDepthM: z.number().nullable(),
  ceilingHeightM: z.number().nullable(),
  rigging: z.string().nullable(),
  pa: z.string().nullable(),
  monitor: z.string().nullable(),
  mixers: z.string().nullable(),
  lights: z.string().nullable(),
  backline: z.string().nullable(),
  power: z.string().nullable(),
  loading: z.string().nullable(),
  nightlinerParking: z.boolean(),
  parking: z.string().nullable(),
  dressingRooms: z.string().nullable(),
  bars: z.number().int().nullable(),
  accessibility: z.string().nullable(),
  wheelchairSpaces: z.number().int().nullable(),
  hasGrandPiano: z.boolean(),
  /** ISO-ukedager, 1 = mandag … 7 = søndag. */
  closedWeekdays: z.array(z.number().int().min(1).max(7)),
  defaultDoors: venueClockSchema.nullable(),
  defaultCurfew: venueClockSchema.nullable(),
  defaultGetIn: venueClockSchema.nullable(),
};

/**
 * SALENS ANSVARLIGE KONTAKTPERSON (251.8): en kobling til en kontakt i husets register, lest fra
 * personen. `contactId` er null når et annet hus leser salen (Spillesteder): de ser hvem de skal
 * ringe, ikke husets register.
 */
export const venueRoomContactSchema = z
  .object({
    contactId: z.uuid().nullable(),
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
  })
  .meta({ id: 'VenueRoomContact' });
export type VenueRoomContact = z.infer<typeof venueRoomContactSchema>;

/** Organisasjonens generelle kontakt, ved siden av salens egen (251.8). */
export const venueHouseContactSchema = z
  .object({
    phone: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
  })
  .meta({ id: 'VenueHouseContact' });
export type VenueHouseContact = z.infer<typeof venueHouseContactSchema>;

export const venueRoomDtoSchema = z
  .object({
    id: z.uuid(),
    festivalId: z.uuid(),
    ...roomFixedShape,
    /**
     * 251.8: salens egen kontaktperson, ved siden av organisasjonens generelle kontakt. API-et
     * fyller den alltid (null uten kontaktperson); valgfri i kontrakten så eldre klienter og
     * testfiksturer uten feltet står.
     */
    contact: venueRoomContactSchema.nullable().optional(),
    /** Hvem utenfor huset ser salen. Salens verdi er den eneste (QA15-S funn 8, 1167). */
    visibility: venueVisibilitySchema,
    /** Ledig/opptatt vises utenfor huset (funn 23). */
    calendarPublished: z.boolean(),
    specVersion: z.number().int(),
    specChangedAt: z.iso.datetime().nullable(),
    specChangedByName: z.string().nullable(),
    sortOrder: z.number().int(),
    layouts: z.array(venueRoomLayoutDtoSchema),
    fields: z.array(venueRoomFieldDtoSchema),
    files: z.array(venueRoomFileDtoSchema),
    crewTemplate: z.array(venueCrewTemplateItemDtoSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    updatedByName: z.string().nullable(),
  })
  .meta({ id: 'VenueRoom' });

export const venueRoomListResponseSchema = z
  .object({
    items: z.array(venueRoomDtoSchema),
  })
  .meta({ id: 'VenueRoomListResponse' });

export const listVenueRoomsQuerySchema = z
  .object({ festivalId: z.uuid() })
  .meta({ id: 'ListVenueRoomsQuery' });

/** Skrivingen av de faste feltene. Alle valgfrie på en PATCH; `name` kreves på en ny sal. */
const roomWriteShape = {
  name: z.string().trim().min(1).max(120),
  description: optionalText(4000),
  city: optionalText(120),
  address: optionalText(240),
  latitude: z.number().min(-90).max(90).nullish(),
  longitude: z.number().min(-180).max(180).nullish(),
  capacityStanding: count(1_000_000).nullish(),
  capacitySeated: count(1_000_000).nullish(),
  stageWidthM: metres.nullish(),
  stageDepthM: metres.nullish(),
  ceilingHeightM: metres.nullish(),
  rigging: optionalText(2000),
  pa: optionalText(2000),
  monitor: optionalText(2000),
  mixers: optionalText(2000),
  lights: optionalText(2000),
  backline: optionalText(2000),
  power: optionalText(2000),
  loading: optionalText(2000),
  nightlinerParking: z.boolean().optional(),
  parking: optionalText(2000),
  dressingRooms: optionalText(2000),
  bars: count(1000).nullish(),
  accessibility: optionalText(2000),
  wheelchairSpaces: count(10_000).nullish(),
  hasGrandPiano: z.boolean().optional(),
  closedWeekdays: z.array(z.number().int().min(1).max(7)).max(7).optional(),
  visibility: venueVisibilitySchema.optional(),
  calendarPublished: z.boolean().optional(),
  defaultDoors: venueClockSchema.nullish(),
  defaultCurfew: venueClockSchema.nullish(),
  defaultGetIn: venueClockSchema.nullish(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  /** 251.8: en kontakt i husets register. `null` tar kontaktpersonen bort. */
  contactId: z.uuid().nullish(),
};

export const createVenueRoomRequestSchema = z
  .object({ festivalId: z.uuid(), ...roomWriteShape })
  .meta({ id: 'CreateVenueRoomRequest' });

export const updateVenueRoomRequestSchema = z
  .object({ ...roomWriteShape, name: roomWriteShape.name.optional() })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenueRoomRequest' });

/**
 * SALENS SYNLIGHET OG PUBLISERTE KALENDER (QA16 301.10). Egen vei med egen celle (`venue:publish`): den
 * som viser salen fram, trenger ikke kunne endre spesifikasjonen, og omvendt.
 */
export const setVenueRoomPublishingRequestSchema = z
  .object({
    visibility: venueVisibilitySchema.optional(),
    calendarPublished: z.boolean().optional(),
  })
  .refine((body) => body.visibility !== undefined || body.calendarPublished !== undefined, {
    message: 'Say what to change.',
  })
  .meta({ id: 'SetVenueRoomPublishingRequest' });
export type SetVenueRoomPublishingRequest = z.infer<typeof setVenueRoomPublishingRequestSchema>;

/* ---------------------------------------------------------------- oppsett, felt, filer, mal */

export const createVenueRoomLayoutRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    capacity: count(1_000_000),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateVenueRoomLayoutRequest' });

export const updateVenueRoomLayoutRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    capacity: count(1_000_000).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenueRoomLayoutRequest' });

export const createVenueRoomFieldRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(120),
    value: shortText(4000).default(''),
  })
  .meta({ id: 'CreateVenueRoomFieldRequest' });

export const updateVenueRoomFieldRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(120).optional(),
    value: shortText(4000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenueRoomFieldRequest' });

/** Rekkefølgen på de egne feltene, som hele lista. En id som mangler beholder plassen sin bakerst. */
export const reorderVenueRoomFieldsRequestSchema = z
  .object({ ids: z.array(z.uuid()).min(1).max(200) })
  .meta({ id: 'ReorderVenueRoomFieldsRequest' });

export const createVenueRoomFileRequestSchema = z
  .object({
    kind: z.enum(VENUE_ROOM_FILE_KINDS),
    title: z.string().trim().min(1).max(200),
    attachmentId: z.uuid().nullish(),
    url: z.url().max(2000).nullish(),
  })
  .refine((body) => (body.kind === 'link' ? body.url != null : body.attachmentId != null), {
    message: 'A link needs a url; an image or a document needs an uploaded file.',
    path: ['url'],
  })
  .meta({ id: 'CreateVenueRoomFileRequest' });

export const updateVenueRoomFileRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenueRoomFileRequest' });

const offset = z
  .number()
  .int()
  .min(-24 * 60)
  .max(24 * 60);

export const createVenueCrewTemplateItemRequestSchema = z
  .object({
    staffingRoleId: z.uuid().nullish(),
    title: z.string().trim().min(1).max(120),
    needed: z.number().int().min(1).max(500).default(1),
    startsOffsetMinutes: offset.default(-120),
    endsOffsetMinutes: offset.default(300),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => body.endsOffsetMinutes > body.startsOffsetMinutes, {
    message: 'The shift must end after it starts.',
    path: ['endsOffsetMinutes'],
  })
  .meta({ id: 'CreateVenueCrewTemplateItemRequest' });

export const updateVenueCrewTemplateItemRequestSchema = z
  .object({
    staffingRoleId: z.uuid().nullish(),
    title: z.string().trim().min(1).max(120).optional(),
    needed: z.number().int().min(1).max(500).optional(),
    startsOffsetMinutes: offset.optional(),
    endsOffsetMinutes: offset.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenueCrewTemplateItemRequest' });

/* ---------------------------------------------------------------------------- leie og synlighet */

/** LEIEN. Bare `venue_rate:read`; vises aldri på Spillesteder. */
export const venueRoomRateDtoSchema = z
  .object({
    venueRoomId: z.uuid(),
    rentMinor: z.number().int().nullable(),
    currency: z.string(),
    merchPercent: z.number().nullable(),
    terms: z.string().nullable(),
    updatedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'VenueRoomRate' });

export const updateVenueRoomRateRequestSchema = z
  .object({
    rentMinor: z.number().int().min(0).max(1_000_000_000_00).nullish(),
    currency: z.string().trim().length(3).toUpperCase().optional(),
    merchPercent: z.number().min(0).max(100).nullish(),
    terms: optionalText(4000),
  })
  .meta({ id: 'UpdateVenueRoomRateRequest' });

/**
 * «SE SOM ARRANGØR» — salen slik et annet hus ser den: spesifikasjonen, oppsettene, de egne feltene,
 * filene og standard kveld. Aldri leien, aldri mannskapsmalen, aldri synligheten.
 */
export const venueRoomSpecDtoSchema = z
  .object({
    id: z.uuid(),
    organizationId: z.uuid(),
    houseName: z.string(),
    venueName: z.string(),
    ...roomFixedShape,
    /** 251.8: salens kontaktperson, som arrangøren ser den. Se `venueRoomDtoSchema`. */
    contact: venueRoomContactSchema.nullable().optional(),
    specVersion: z.number().int(),
    specChangedAt: z.iso.datetime().nullable(),
    layouts: z.array(venueRoomLayoutDtoSchema),
    fields: z.array(venueRoomFieldDtoSchema),
    files: z.array(venueRoomFileDtoSchema),
  })
  .meta({ id: 'VenueRoomSpec' });

/* ------------------------------------------------------------------------------------ kveldene */

/**
 * STIGEN (C4, 1185): kvelden har konsertens ordforråd. Basen avleder trinnet (`app.venue_night_status`)
 * fra køen, budet og innslagene; Annonsert, I salg og Oppgjort settes for hånd, Avlyst med «Avlys».
 */
export const VENUE_NIGHT_STATUSES = [
  'requested',
  'hold',
  'confirmed',
  'announced',
  'on_sale',
  'performed',
  'settled',
  'declined',
  'cancelled',
] as const;
/** Det en bruker kan sette selv. `confirmed` betyr «tilbake til det basen avleder» (gjenopprett). */
export const MANUAL_VENUE_NIGHT_STATUSES = [
  'confirmed',
  'announced',
  'on_sale',
  'settled',
  'cancelled',
] as const;
export type ManualVenueNightStatus = (typeof MANUAL_VENUE_NIGHT_STATUSES)[number];
/** Husets eget, eller utleie til en arrangør (C1, 1185). */
export const VENUE_NIGHT_MODES = ['own', 'rental'] as const;
export type VenueNightMode = (typeof VENUE_NIGHT_MODES)[number];

/** Gjentakelsene et spillested bruker (F41): fast klubbkveld hver torsdag, annenhver, månedlig. */
export const VENUE_NIGHT_REPEATS = ['week', 'two_weeks', 'month'] as const;
export type VenueNightRepeat = (typeof VENUE_NIGHT_REPEATS)[number];
/** Taket på én serie: to år med ukentlige arrangementer. */
export const VENUE_NIGHT_SERIES_MAX = 106;
export type VenueNightStatus = (typeof VENUE_NIGHT_STATUSES)[number];
export const venueNightStatusSchema = z.enum(VENUE_NIGHT_STATUSES).meta({ id: 'VenueNightStatus' });

export const venueNightDtoSchema = z
  .object({
    id: z.uuid(),
    festivalId: z.uuid(),
    editionId: z.uuid(),
    venueRoomId: z.uuid(),
    roomName: z.string(),
    nightOn: z.iso.date(),
    title: z.string().nullable(),
    doorsAt: z.iso.datetime().nullable(),
    curfewAt: z.iso.datetime().nullable(),
    getInAt: z.iso.datetime().nullable(),
    /** De tre over som lokal tid i årgangens sone, for visning og skjema. */
    doorsTime: venueClockSchema.nullable(),
    curfewTime: venueClockSchema.nullable(),
    getInTime: venueClockSchema.nullable(),
    timezone: z.string(),
    layoutName: z.string().nullable(),
    capacity: z.number().int().nullable(),
    status: venueNightStatusSchema,
    venueEnquiryId: z.uuid().nullable(),
    promoterOrganizationId: z.uuid().nullable(),
    promoterName: z.string().nullable(),
    artistName: z.string().nullable(),
    /** Arrangørens konsert, i arrangørens hus. Satt når kvelden kom fra en bekreftet datoforespørsel. */
    linkedConcertId: z.uuid().nullable(),
    /** Programposten på arrangementet (sak 230, 0455): fra et godtatt bud eller «Legg til opptredende». */
    performanceId: z.uuid().nullable(),
    notes: z.string().nullable(),
    /** Spillestedet (beholderens navn): tittelen er «innslag @ spillested – sal» (funn 21). */
    venueName: z.string(),
    /** C1: husets eget eller utleie. */
    productionMode: z.enum(VENUE_NIGHT_MODES),
    /** B1: budet arrangementet følger eller kom fra. */
    bookingBidId: z.uuid().nullable(),
    /** C1: arrangøren i firmaregisteret. */
    promoterCompanyId: z.uuid().nullable(),
    /** Innslagene i rekkefølge, hovedinnslaget først — for utleie arrangørens. Navn, aldri lagret. */
    performerNames: z.array(z.string()),
    shiftCount: z.number().int(),
    /** Serien arrangementet er en del av (F41): de gjentakende arrangementene deler den. */
    seriesId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'VenueNight' });

export const venueNightShiftDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    roleName: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    needed: z.number().int(),
    assigned: z.number().int(),
    /** Funn 29: avlyst med arrangementet (eller for hånd) — teller ikke som manglende folk. */
    cancelled: z.boolean(),
  })
  .meta({ id: 'VenueNightShift' });

/** Serien sett fra ett av arrangementene (F41/F49): hvor mange det er, og første og siste dato. */
export const venueNightSeriesDtoSchema = z
  .object({
    id: z.uuid(),
    count: z.number().int(),
    firstOn: z.iso.date(),
    lastOn: z.iso.date(),
    /** Hvor mange som kommer etter dette, i datoorden. */
    laterCount: z.number().int(),
  })
  .meta({ id: 'VenueNightSeries' });

/**
 * ET INNSLAG PÅ ARRANGEMENTET (B1, 1185): en programpost, aldri en tekst. `bookingArtistId` er
 * Booking-oppføringen (lenken i Oversikt), `agencyName` byrået, `deal` avtalen i én linje.
 */
export const venueNightPerformerDtoSchema = z
  .object({
    performanceId: z.uuid(),
    name: z.string(),
    billing: z.enum(['headliner', 'support']).nullable(),
    bookingArtistId: z.uuid().nullable(),
    agencyName: z.string().nullable(),
    /** Saken i Booking (0927): not_asked … signed, cancelled. Null for et lokalt innslag. */
    engagementStatus: z.string().nullable(),
    /**
     * Avtaletypen og honoraret: fra det godtatte avtalearket, ellers fra budet som er ute (QA16 funn 2).
     * Beløpet er null når leseren ikke ser beløp.
     */
    dealType: z.enum(CONCERT_DEAL_TYPES).nullable(),
    feeMinor: z.number().int().nullable(),
    currency: z.string().nullable(),
    /** QA16 funn 12: the share and how it meets the guarantee, so a versus is read in full. */
    feeTerms: z
      .object({
        artistPercent: z.number().nullable(),
        versusMode: z.enum(['higher_of', 'guarantee_plus']).nullable(),
        versusBasis: z.enum(['net', 'gross']).nullable(),
        afterCosts: z.boolean().nullable(),
      })
      .nullable()
      .default(null),
    /** Budet som henger på innslaget (QA16 funn 2), det siste. Null for et innslag uten bud. */
    bidId: z.uuid().nullable(),
    bidStatus: bookingBidStatusSchema.nullable(),
    cancelled: z.boolean(),
    /** Reise eller overnatting ble lagt inn før arrangementet ble flyttet eller avlyst. */
    needsReview: z.boolean(),
    /**
     * Funn 36: byrået ser advancen (og reisefølget og avtalen) fordi budet ble godtatt. «Del
     * arrangementet» viser det som en part, ikke «Ikke delt med noen».
     */
    sharedWithAgency: z.boolean(),
  })
  .meta({ id: 'VenueNightPerformer' });
export type VenueNightPerformerDto = z.infer<typeof venueNightPerformerDtoSchema>;

/**
 * DET SOM ER AVTALT (C2): den godtatte versjonen av avtalearket. Arrangementet bærer det som gjelder;
 * Oversikt viser avviket diskré («Avtalen sier 1 500»). Null uten en godtatt avtale.
 */
export const venueNightAgreedDtoSchema = z
  .object({
    nightOn: z.iso.date().nullable(),
    capacity: z.number().int().nullable(),
    startsAt: venueClockSchema.nullable(),
  })
  .meta({ id: 'VenueNightAgreed' });

export const venueNightDetailDtoSchema = venueNightDtoSchema
  .extend({
    shifts: z.array(venueNightShiftDtoSchema),
    series: venueNightSeriesDtoSchema.nullable(),
    performers: z.array(venueNightPerformerDtoSchema),
    agreed: venueNightAgreedDtoSchema.nullable(),
    /** Utleie til en organisasjon i Tvitvi: arrangørens konsert er opprettet hos dem. */
    promoterInTvitvi: z.boolean(),
    /** Malen arrangementet fikk (B6). Null: ingen mal er brukt. */
    template: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  })
  .meta({ id: 'VenueNightDetail' });

export const VENUE_NIGHT_SORT_KEYS = ['date'] as const;

/**
 * ARRANGEMENTENE I EN PERIODE, IKKE I ET ÅR (sak 229). Et spillested har ingen årgang i
 * brukerflaten; lista spør «fra og med, til og med» på datoen, og en periode over nyttår er én
 * spørring. Begge er valgfrie: utelatt `from` er alt før `to`, utelatt `to` alt etter `from`.
 */
export const listVenueNightsQuerySchema = cursorQuerySchema(VENUE_NIGHT_SORT_KEYS)
  .extend({
    festivalId: z.uuid(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    roomId: z.uuid().optional(),
    status: venueNightStatusSchema.optional(),
    /** Søk i tittel, artist, arrangør og sal (F35). */
    q: z.string().trim().max(200).optional(),
  })
  .refine((query) => query.from === undefined || query.to === undefined || query.from <= query.to, {
    message: 'from must not be after to.',
    path: ['to'],
  })
  .meta({ id: 'ListVenueNightsQuery' });

export const venueNightListResponseSchema = page(venueNightDtoSchema, 'VenueNightListResponse');

/* ------------------------------------------------------------------ «Hvem opptrer?» (B1, 1185) */

/* «Avtalen er gjort utenfor Tvitvi» står i `org-concerts.ts` (QA16 funn 31): konserten bruker den også. */
export { outsideDealSchema, type OutsideDeal };

/**
 * DE FEM VEIENE, ÉN FORM. `bid`: et godtatt bud (sal, dato, avtale og byrå følger med). `outside`:
 * en artist i Booking (eller fra registeret — laget lages da) med avtalen gjort utenfor Tvitvi.
 * `artist` (QA16 funn 2): artisten er valgt og budet kommer etterpå («Send bud nå»); innslaget står på
 * arrangementet fra det øyeblikket, og budet henger seg på det. `send_bid`: et bud som alt er sendt;
 * det henger seg på innslaget. `local`: lokalt, uten byrå. «Ikke avklart ennå» er ingen verdi.
 */
export const eventPerformerChoiceSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('bid'), bidId: z.uuid() }),
    z.object({ kind: z.literal('outside'), bookingArtistId: z.uuid(), deal: outsideDealSchema }),
    z.object({ kind: z.literal('artist'), bookingArtistId: z.uuid() }),
    z.object({ kind: z.literal('send_bid'), bidId: z.uuid() }),
    z.object({ kind: z.literal('local'), name: z.string().trim().min(1).max(200) }),
  ])
  .meta({ id: 'EventPerformerChoice' });
export type EventPerformerChoice = z.infer<typeof eventPerformerChoiceSchema>;

export const addEventPerformerRequestSchema = z
  .object({
    performer: eventPerformerChoiceSchema,
    /** Utelatt: hovedinnslag når arrangementet ikke har et, ellers support. */
    billing: z.enum(['headliner', 'support']).optional(),
  })
  .meta({ id: 'AddEventPerformerRequest' });
export type AddEventPerformerRequest = z.infer<typeof addEventPerformerRequestSchema>;

/** Arrangøren ved utleie (C1): en organisasjon i Tvitvi eller en rad i firmaregisteret. */
export const eventPromoterChoiceSchema = z
  .union([z.object({ organizationId: z.uuid() }), z.object({ companyId: z.uuid() })])
  .meta({ id: 'EventPromoterChoice' });
export type EventPromoterChoice = z.infer<typeof eventPromoterChoiceSchema>;

export const eventPromoterOptionSchema = z
  .object({
    /** Organisasjonen i Tvitvi, når den er det (også et firma som er koblet til en). */
    organizationId: z.uuid().nullable(),
    companyId: z.uuid().nullable(),
    name: z.string(),
    /** Organisasjonen har Konserter: konserten lages hos dem, og de legger inn innslaget. */
    hasConcerts: z.boolean(),
  })
  .meta({ id: 'EventPromoterOption' });
export type EventPromoterOption = z.infer<typeof eventPromoterOptionSchema>;
export const eventPromoterOptionsQuerySchema = z
  .object({ q: z.string().trim().max(120).default('') })
  .meta({ id: 'EventPromoterOptionsQuery' });
export const eventPromoterOptionsResponseSchema = z
  .object({ items: z.array(eventPromoterOptionSchema) })
  .meta({ id: 'EventPromoterOptions' });
export type EventPromoterOptions = z.infer<typeof eventPromoterOptionsResponseSchema>;

/** Funn 37: arrangementene en artist i Booking har hos oss — husets kvelder og arrangørens konserter. */
export const artistOwnEventSchema = z
  .object({
    kind: z.enum(['venue_night', 'concert']),
    id: z.uuid(),
    /** Spillestedet (for adressen `/venue/$festivalId/events/$id`), null for en konsert. */
    festivalId: z.uuid().nullable(),
    on: z.iso.date(),
    status: z.string(),
    venueName: z.string().nullable(),
    roomName: z.string().nullable(),
  })
  .meta({ id: 'ArtistOwnEvent' });
export type ArtistOwnEvent = z.infer<typeof artistOwnEventSchema>;
export const artistOwnEventsResponseSchema = z
  .object({ upcoming: z.array(artistOwnEventSchema), past: z.array(artistOwnEventSchema) })
  .meta({ id: 'ArtistOwnEvents' });
export type ArtistOwnEvents = z.infer<typeof artistOwnEventsResponseSchema>;

export const createVenueNightRequestSchema = z
  .object({
    festivalId: z.uuid(),
    venueRoomId: z.uuid(),
    nightOn: z.iso.date(),
    title: optionalText(200),
    /** Utelatt: salens standard kveld. `null`: ingen tid. */
    doorsTime: venueClockSchema.nullish(),
    curfewTime: venueClockSchema.nullish(),
    getInTime: venueClockSchema.nullish(),
    layoutName: optionalText(80),
    capacity: count(1_000_000).nullish(),
    artistName: optionalText(200),
    promoterName: optionalText(200),
    notes: optionalText(4000),
    /** Bruk salens standardmal med én gang. Gjelder bare når `template` er utelatt. */
    withCrew: z.boolean().default(true),
    /** C1: `own` (standard) eller `rental` med `promoter`. */
    mode: z.enum(VENUE_NIGHT_MODES).optional(),
    promoter: eventPromoterChoiceSchema.optional(),
    /** B1: innslaget. Utelatt er «Ikke avklart ennå». Ikke for utleie (arrangøren legger det inn). */
    performer: eventPerformerChoiceSchema.optional(),
    /**
     * MALEN (B6, grensesnitt 5), brukt i samme transaksjon som arrangementet lages — på hvert
     * arrangement i en serie. `templateId` null er salens standardmal; `content` er linjene slik de ble
     * godtatt i forhåndsvisningen. `null`: ingen mal. Utelatt: `withCrew` bestemmer (standardmalen eller
     * ingen). Med en mal settes get-in og curfew av malen, målt fra dørene, når de ikke er sendt.
     */
    template: z
      .object({
        templateId: z.uuid().nullable(),
        content: concertTemplateContentSchema.optional(),
      })
      .nullish(),
    /**
     * GJENTAKELSEN (F41): ett arrangement per uke, annenhver uke eller måned, fra `nightOn` til og
     * med `until`. Atle: «Må ha en start og stopp dato» — derfor er sluttdatoen påkrevd.
     */
    repeat: z
      .object({
        every: z.enum(VENUE_NIGHT_REPEATS),
        until: z.iso.date(),
      })
      .optional(),
  })
  .refine((body) => body.repeat === undefined || body.repeat.until > body.nightOn, {
    message: 'The series must end after it starts.',
    path: ['repeat', 'until'],
  })
  .refine((body) => body.mode !== 'rental' || body.performer === undefined, {
    message: 'A rental gets its act from the promoter.',
    path: ['performer'],
  })
  .refine((body) => body.mode !== 'rental' || body.promoter !== undefined, {
    message: 'Pick the promoter.',
    path: ['promoter'],
  })
  .meta({ id: 'CreateVenueNightRequest' });

export const updateVenueNightRequestSchema = z
  .object({
    venueRoomId: z.uuid().optional(),
    nightOn: z.iso.date().optional(),
    title: optionalText(200),
    doorsTime: venueClockSchema.nullish(),
    curfewTime: venueClockSchema.nullish(),
    getInTime: venueClockSchema.nullish(),
    layoutName: optionalText(80),
    capacity: count(1_000_000).nullish(),
    status: z.enum(MANUAL_VENUE_NIGHT_STATUSES).optional(),
    artistName: optionalText(200),
    promoterName: optionalText(200),
    notes: optionalText(4000),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenueNightRequest' });

/** Hvilke av seriens arrangementer en endring eller sletting gjelder (F49). */
export const VENUE_NIGHT_SCOPES = ['this', 'following', 'all'] as const;
export type VenueNightScope = (typeof VENUE_NIGHT_SCOPES)[number];
export const venueNightScopeQuerySchema = z
  .object({ scope: z.enum(VENUE_NIGHT_SCOPES).default('this') })
  .meta({ id: 'VenueNightScopeQuery' });

/** `PATCH /venue-nights/:id/series`: arrangementene endringen traff. */
export const venueNightSeriesUpdateDtoSchema = z
  .object({ items: z.array(venueNightDtoSchema) })
  .meta({ id: 'VenueNightSeriesUpdate' });

/** `DELETE /venue-nights/:id/series`: kvitteringen «Angre» sender tilbake. */
export const venueNightSeriesDeletionDtoSchema = z
  .object({ ids: z.array(z.uuid()) })
  .meta({ id: 'VenueNightSeriesDeletion' });

export const restoreVenueNightsRequestSchema = z
  .object({ ids: z.array(z.uuid()).min(1).max(VENUE_NIGHT_SERIES_MAX) })
  .meta({ id: 'RestoreVenueNightsRequest' });

/** «Kopier arrangement» (F41): samme innhold, ny dato, og eventuelt en annen sal. */
export const copyVenueNightRequestSchema = z
  .object({
    nightOn: z.iso.date(),
    venueRoomId: z.uuid().optional(),
    /** Vaktene på arrangementet følger med, uten folk. */
    withShifts: z.boolean().default(true),
  })
  .meta({ id: 'CopyVenueNightRequest' });

export const venueNightShiftsCreatedDtoSchema = z
  .object({ created: z.number().int() })
  .meta({ id: 'VenueNightShiftsCreated' });

/** Spillestedets Oversikt: kommende kvelder og ubesvarte datoforespørsler. */
export const venueOverviewDtoSchema = z
  .object({
    festivalId: z.uuid(),
    roomCount: z.number().int(),
    upcoming: z.array(venueNightDtoSchema),
    /** Datoforespørsler med status «new». Tellingen stopper på 100 («99+» i skjermen). */
    openEnquiries: z.number().int(),
  })
  .meta({ id: 'VenueOverview' });

export type VenueRoomLayoutDto = z.infer<typeof venueRoomLayoutDtoSchema>;
export type VenueRoomFieldDto = z.infer<typeof venueRoomFieldDtoSchema>;
export type VenueRoomFileDto = z.infer<typeof venueRoomFileDtoSchema>;
export type VenueCrewTemplateItemDto = z.infer<typeof venueCrewTemplateItemDtoSchema>;
export type VenueRoomDto = z.infer<typeof venueRoomDtoSchema>;
export type VenueRoomListResponse = z.infer<typeof venueRoomListResponseSchema>;
export type ListVenueRoomsQuery = z.infer<typeof listVenueRoomsQuerySchema>;
export type CreateVenueRoomRequest = z.infer<typeof createVenueRoomRequestSchema>;
export type UpdateVenueRoomRequest = z.infer<typeof updateVenueRoomRequestSchema>;
export type CreateVenueRoomLayoutRequest = z.infer<typeof createVenueRoomLayoutRequestSchema>;
export type UpdateVenueRoomLayoutRequest = z.infer<typeof updateVenueRoomLayoutRequestSchema>;
export type CreateVenueRoomFieldRequest = z.infer<typeof createVenueRoomFieldRequestSchema>;
export type UpdateVenueRoomFieldRequest = z.infer<typeof updateVenueRoomFieldRequestSchema>;
export type ReorderVenueRoomFieldsRequest = z.infer<typeof reorderVenueRoomFieldsRequestSchema>;
export type CreateVenueRoomFileRequest = z.infer<typeof createVenueRoomFileRequestSchema>;
export type UpdateVenueRoomFileRequest = z.infer<typeof updateVenueRoomFileRequestSchema>;
export type CreateVenueCrewTemplateItemRequest = z.infer<
  typeof createVenueCrewTemplateItemRequestSchema
>;
export type UpdateVenueCrewTemplateItemRequest = z.infer<
  typeof updateVenueCrewTemplateItemRequestSchema
>;
export type VenueRoomRateDto = z.infer<typeof venueRoomRateDtoSchema>;
export type UpdateVenueRoomRateRequest = z.infer<typeof updateVenueRoomRateRequestSchema>;
export type VenueRoomSpecDto = z.infer<typeof venueRoomSpecDtoSchema>;
export type VenueNightDto = z.infer<typeof venueNightDtoSchema>;
export type VenueNightShiftDto = z.infer<typeof venueNightShiftDtoSchema>;
export type VenueNightDetailDto = z.infer<typeof venueNightDetailDtoSchema>;
export type ListVenueNightsQuery = z.infer<typeof listVenueNightsQuerySchema>;
export type VenueNightListResponse = z.infer<typeof venueNightListResponseSchema>;
export type CreateVenueNightRequest = z.infer<typeof createVenueNightRequestSchema>;
export type UpdateVenueNightRequest = z.infer<typeof updateVenueNightRequestSchema>;
export type VenueNightShiftsCreatedDto = z.infer<typeof venueNightShiftsCreatedDtoSchema>;
export type VenueNightSeriesDto = z.infer<typeof venueNightSeriesDtoSchema>;
export type VenueNightScopeQuery = z.infer<typeof venueNightScopeQuerySchema>;
export type VenueNightSeriesUpdateDto = z.infer<typeof venueNightSeriesUpdateDtoSchema>;
export type VenueNightSeriesDeletionDto = z.infer<typeof venueNightSeriesDeletionDtoSchema>;
export type RestoreVenueNightsRequest = z.infer<typeof restoreVenueNightsRequestSchema>;
export type CopyVenueNightRequest = z.infer<typeof copyVenueNightRequestSchema>;
export type VenueOverviewDto = z.infer<typeof venueOverviewDtoSchema>;
